import { Component } from 'react';

import Button from 'honeydew/Button';
import logError from 'utils/errorUtils';

class EBTPaymentFlowErrorBoundary extends Component {
    constructor(props) {
        super(props);
        this.state = {
            hasError: false,
            error: null,
            errorInfo: null,
        };
    }

    static getDerivedStateFromError(error) {
        // Update state so the next render will show the fallback UI
        return {
            hasError: true,
            error,
        };
    }

    componentDidCatch(error, errorInfo) {
        const { currentStep } = this.props;

        // Log the error to our error reporting service
        this.setState({
            errorInfo,
        });

        const errorDetails = {
            component: 'EBTPaymentFlowErrorBoundary',
            step: currentStep || 'unknown',
            errorInfo,
        };

        // Log to Sentry
        logError(new Error(error), errorDetails);
    }

    handleRetry = () => {
        const { onRetry } = this.props;

        this.setState({
            hasError: false,
            error: null,
            errorInfo: null,
        });

        onRetry?.();
    };

    handleClose = () => {
        const { onClose } = this.props;

        this.setState({
            hasError: false,
            error: null,
            errorInfo: null,
        });
        onClose?.();
    };

    render() {
        const { error, errorInfo, hasError } = this.state;
        const { children } = this.props;

        if (hasError) {
            return (
                <div className="space-y-10">
                    <h2 className="text-body-lg font-grotesk-bold text-error">Sorry, an unexpected error occurred.</h2>

                    <p className="text-body-sm">
                        We&apos;ve been notified of the issue and will investigate it shortly. In the meantime, please
                        try again or contact support.
                    </p>

                    {process.env.NODE_ENV === 'development' && errorInfo && (
                        <div className="space-y-5 border border-dashed p-10">
                            <p className="text-body-sm">Technical Details (development only)</p>
                            <details className="text-body-xs" open>
                                <summary className="cursor-pointer font-grotesk-bold">Error Message</summary>
                                <p className="mt-10 bg-[#efefef] p-10 wrap-break-word whitespace-pre-wrap">
                                    {error?.message || 'An unexpected error occurred'}
                                </p>
                            </details>
                            <details className="text-body-xs">
                                <summary className="cursor-pointer font-grotesk-bold">Stack Trace</summary>
                                <pre className="mt-10 bg-[#efefef] p-10 wrap-break-word whitespace-pre-wrap">
                                    {errorInfo.componentStack}
                                </pre>
                            </details>
                        </div>
                    )}

                    <div className="flex flex-col gap-10 pt-5 sm:flex-row sm:justify-center">
                        <Button variant="primary" onClick={this.handleRetry}>
                            Try Again
                        </Button>
                        <Button variant="secondary" onClick={this.handleClose}>
                            Close
                        </Button>
                    </div>
                </div>
            );
        }

        return children;
    }
}

function ErrorComponent({ error }) {
    throw new Error(error);
}

export { ErrorComponent };

export default EBTPaymentFlowErrorBoundary;
